<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Usuários</v-toolbar-title>
                    <v-spacer/>
                    <v-text-field
                        dense
                        clearable
                        outlined
                        single-line
                        hide-details
                        v-model="search"
                        @keypress.enter="consultar"
                        label="Pesquise.."
                        append-icon="mdi-magnify"
                    />
                    <v-spacer/>
                    <v-select
                        dense
                        outlined
                        multiple
                        clearable
                        small-chips
                        hide-details
                        v-model="tipos"
                        :items="tiposDisponiveis"
                        placeholder="Tipo"
                        style="max-width: 220px;"
                        @change="onChangeTipos"
                    >
                        <template v-slot:selection="{ item, index }">
                            <v-chip v-if="index === 0" x-small>
                                <span>{{ item.text }}</span>
                            </v-chip>
                            <span v-if="index === 1" class="grey--text text-caption">
                            (+{{ tipos.length - 1 }} outros)
                            </span>
                        </template>
                    </v-select>
                    <v-spacer/>
                    <v-btn
                        fab
                        small
                        color="primary"
                        title="Novo usuário"
                        v-if="checkPermission('zapercontrol_usuarios_adicionar')"
                        @click="usuarioDialogVisible = true"
                    >
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card elevation="2" class="ma-1">
                    <v-card-text>
                        <v-data-table
                            fixed-header
                            :headers="headers"
                            :items="dados"
                            sort-by="id"
                            :mobileBreakpoint="0"
                            :loading="loading"
                            :height="$vuetify.breakpoint.height - 230"
                            :disable-pagination="true"
                            :hide-default-footer="true"
                            :options.sync="options"
                        >
                            <template v-slot:item.empresa_id="{ item }">
                                <span v-if="item.tipo !== 'suporte'">
                                    {{ item.empresa_id }}
                                </span>
                                <v-text-field
                                    v-else
                                    dense
                                    outlined
                                    single-line
                                    hide-details
                                    v-model="item.empresa_id"
                                    @keypress.enter="updateUserEmp(item)"
                                    style="width: 100px;"
                                />
                            </template>
                            <template v-slot:item.status="{ item }">
                                <v-chip
                                    x-small
                                    dark
                                    :color="item.status === 'ativo' ? 'success' : 'grey'"
                                >
                                    {{ item.status === 'ativo' ? 'Ativo' : 'Inativo' }}
                                </v-chip>

                                <template v-if="podeGerenciar(item)">
                                    <v-btn
                                        icon
                                        small
                                        :loading="acaoEmCurso === item.id"
                                        :title="item.status === 'ativo' ? 'Inativar usuário' : 'Ativar usuário'"
                                        @click="alterarStatus(item)"
                                    >
                                        <v-icon small>
                                            {{ item.status === 'ativo'
                                                ? 'mdi-account-off-outline'
                                                : 'mdi-account-check-outline' }}
                                        </v-icon>
                                    </v-btn>

                                    <v-btn
                                        icon
                                        small
                                        v-if="item.status === 'ativo'"
                                        :loading="acaoEmCurso === item.id"
                                        title="Derrubar sessões ativas"
                                        @click="revogarSessoes(item)"
                                    >
                                        <v-icon small>mdi-logout-variant</v-icon>
                                    </v-btn>
                                </template>
                            </template>
                            <template v-slot:item.created_at="{ item }">
                                {{ formatDate(item.created_at) }}
                            </template>
                            <template v-slot:item.permissions="{ item }">
                                <v-btn
                                    v-if="['suporte', 'diretor', 'gestor'].includes(item.tipo)"
                                    @click="onPermissionsClick(item)"
                                    icon
                                >
                                    <v-icon>mdi-shield-account-outline</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>
                        <Paginate v-model="pagination" @input="consultar"/>
                    </v-card-text>
                </v-card>
            </v-content>

            <PermissionsDialog
                v-if="permissionsDialogVisible && checkPermission('zapercontrol_usuarios_permissoes_consultar')"
                v-model="userId"
                @close="permissionsDialogVisible = false"
            />

            <UsuarioDialog
                :visible="usuarioDialogVisible"
                @close="usuarioDialogVisible = false"
                @onCadastro="onUsuarioCriado"
            />
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import Paginate from '@/components/Paginate';
import PermissionsDialog from './PermissionDialog';
import UsuarioDialog from './UsuarioDialog';

const CHAVE_FILTROS = 'zaper-control-usuarios-filters';

export default {
    name: 'Usuarios',

    components: {
        BaseContainer,
        Paginate,
        PermissionsDialog,
        UsuarioDialog,
    },

    data: () => ({
        search: '',
        permissionsDialogVisible: false,
        usuarioDialogVisible: false,
        userId: null,
        loading: false,
        acaoEmCurso: null,
        dados: [],
        tipos: [],
        tiposDisponiveis: [
            { text: 'Cliente', value: 'cliente' },
            { text: 'Consumidor', value: 'consumidor' },
            { text: 'Diretor', value: 'diretor' },
            { text: 'Gestor', value: 'gestor' },
            { text: 'Suporte', value: 'suporte' },
        ],
        headers: [
            { text: 'ID', value: 'id' },
            { text: 'Nome', value: 'name' },
            { text: 'E-mail', value: 'email' },
            { text: 'Empresa ID', value: 'empresa_id' },
            { text: 'Tipo', value: 'tipo' },
            { text: 'Status', value: 'status', sortable: false },
            { text: 'Criado em', value: 'created_at' },
            { text: 'Permissões', value: 'permissions' },
        ],
        pagination: {
            current_page: 1,
            last_page: 1,
            total: 0,
        },
        options: {},
    }),

    mounted() {
        this.getFilters();
        this.consultar();
    },

    watch: {
        options: {
            async handler() {
                this.consultar();
            },
            deep: true,
        },
    },

    methods: {
        getParams() {
            const params = new URLSearchParams();

            params.append('page', this.pagination.current_page);
            params.append('q', this.search || '');

            if (this.options.sortBy && this.options.sortBy.length) {
                params.append('sortField', this.options.sortBy[0]);
            }
            if (this.options.sortDesc && this.options.sortDesc.length) {
                params.append('sortOrder', this.options.sortDesc[0] ? 1 : -1);
            }

            (this.tipos || []).forEach(tipo => params.append('tipos[]', tipo));

            return params.toString();
        },

        consultar() {
            if (!this.checkPermission('zapercontrol_usuarios_consultar')) {
                alert('Usuário sem permissão');
                return;
            }

            this.setFilters();

            this.loading = true;
            this.dados = [];

            this.$http.post(`usuarios/get-all?${this.getParams()}`).then(resp => {
                this.pagination = resp.data.meta;
                this.dados = resp.data.data;
                this.loading = false;
            })
            .catch(() => {
                alert('Não foi possivel concluir a operação');
                this.loading = false;
            });
        },

        onChangeTipos() {
            this.pagination.current_page = 1;
            this.consultar();
        },

        setFilters() {
            localStorage.setItem(CHAVE_FILTROS, JSON.stringify({ tipos: this.tipos || [] }));
        },

        getFilters() {
            try {
                const filtros = JSON.parse(localStorage.getItem(CHAVE_FILTROS));
                this.tipos = (filtros && filtros.tipos) || [];
            } catch (e) {
                this.tipos = [];
            }
        },

        podeGerenciar(item) {
            return ['gestor', 'diretor'].includes(item.tipo)
                && item.id !== this.$store.state.usuario.id
                && this.checkPermission('zapercontrol_usuarios_status_atualizar');
        },

        async alterarStatus(item) {
            const inativando = item.status === 'ativo';

            const titulo = inativando ? 'Inativar usuário' : 'Ativar usuário';
            const mensagem = inativando
                ? `${item.name} perderá o acesso imediatamente e todas as sessões abertas serão derrubadas. Confirma?`
                : `${item.name} voltará a conseguir entrar no sistema. Confirma?`;

            if (!await this.$root.$confirm(titulo, mensagem)) {
                return;
            }

            const status = inativando ? 'inativo' : 'ativo';

            this.acaoEmCurso = item.id;

            try {
                const { data } = await this.$http.post(`usuarios/${item.id}/status`, { status });

                this.notify(data.msg, data.type);

                if (data.type === 'success') {
                    item.status = status;
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.acaoEmCurso = null;
            }
        },

        async revogarSessoes(item) {
            const confirmado = await this.$root.$confirm(
                'Derrubar sessões',
                `${item.name} será desconectado de todos os aparelhos e precisará entrar de novo. Confirma?`,
            );

            if (!confirmado) {
                return;
            }

            this.acaoEmCurso = item.id;

            try {
                const { data } = await this.$http.post(`usuarios/${item.id}/revogar-sessoes`);
                this.notify(data.msg, data.type);
            } catch (e) {
                console.log(e);
            } finally {
                this.acaoEmCurso = null;
            }
        },

        onUsuarioCriado(usuario) {
            this.usuarioDialogVisible = false;
            this.consultar();

            if (usuario && this.checkPermission('zapercontrol_usuarios_permissoes_atualizar')) {
                this.userId = usuario.id;
                this.permissionsDialogVisible = true;
            }
        },

        updateUserEmp({ empresa_id, id: user_id}) {
            this.$http.post('usuarios/update-emp', { empresa_id, user_id}).then(() => {
                alert('Empresa atualizada com sucesso');
            })
            .catch(() => {
                alert('Não foi possivel concluir a operação');
                this.loading = false;
            });
        },

        onPermissionsClick(user) {
            // if (!this.checkPermission('zapercontrol_usuarios_permissoes_consultar')) {
            //     alert('Usuário sem permissão');
            //     return;
            // }
            this.userId = user.id;
            this.permissionsDialogVisible = true;
        },
    },
}
</script>
